import type {
  FAdvancedImage,
  FImageMetadata,
  FUser,
  ICreateImagePlaceholderRequest,
  ICreateImagePlaceholderResponse,
} from "@piperunner-cms/types/v1";
import { collection, deleteDoc, doc, getDoc, getDocs, query, setDoc, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  deleteObject,
  type FirebaseStorage,
  type FullMetadata,
  getDownloadURL,
  getMetadata,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { FIRESTORE_COLLECTION_PATH, FolderNames } from "@/constants";
import { fsDb } from "./utils/initialize-fs-db";
import { functions } from "./utils/initialize-functions";
import { mediaStorage } from "./utils/initialize-storage";

export const extractFirebaseFileName = (url: string): string => {
  try {
    // Extract the 'o' parameter which contains the encoded file path
    const urlObj = new URL(url);

    if (urlObj.pathname) {
      // Decode URL encoding (%2F becomes /, etc.)
      const decodedPath = decodeURIComponent(urlObj.pathname);
      // Extract just the filename from the path
      return decodedPath.split("/").pop() || "unknown";
    }
    return "unknown";
  } catch {
    return "unknown";
  }
};

export const deleteImage = async (filePath: string, bucket: FirebaseStorage) => {
  const fileRef = ref(bucket, filePath);
  await deleteObject(fileRef);
};

type GetFileUrlResult = {
  url: string;
} & FullMetadata;

const getFileURL = async (name: string, bucket: FirebaseStorage): Promise<GetFileUrlResult> => {
  const fileRef = ref(bucket, name);

  const downloadUrl = await getDownloadURL(fileRef);
  const metadata = await getMetadata(fileRef);

  return {
    url: downloadUrl,
    ...metadata,
  };
};

type UploadFileProps = {
  file: File;
  fileName?: string;
  extension?: string | null; // null means no extension, undefined means use original file extension
  progressCb?: (progress: number) => void;
  firebaseStorageObj: FirebaseStorage;
  // Should not contain terminating slash
  folder?: string;
} & Partial<FullMetadata>;

export const uploadFile = async ({
  file,
  fileName, // used for renaming file
  extension: _extension,
  progressCb,
  firebaseStorageObj,
  folder,
  ...metadata
}: UploadFileProps): Promise<GetFileUrlResult> => {
  const name = fileName ?? `${file.name.split(".").slice(0, -1).join(".")}_${uuidv4()}`;
  const extension = _extension === null ? undefined : _extension || file.name.split(".").pop();

  const folderPrefix = folder ? `${folder}/` : "";
  const extensionPostfix = extension ? `.${extension}` : "";
  const fullPath = `${folderPrefix}${name}${extensionPostfix}`;

  const fileRef = ref(firebaseStorageObj, fullPath);
  const uploadTask = uploadBytesResumable(fileRef, file, metadata);

  return new Promise((resolve, reject) => {
    uploadTask.on("state_changed", {
      next: (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        progressCb?.(progress);
      },
      complete: () => {
        getFileURL(fullPath, firebaseStorageObj)
          .then((response) => resolve(response))
          .catch((error) => {
            console.error(error);
            reject(new Error(error instanceof Error ? error.message : String(error)));
          });
      },
      error: (error) => {
        console.error(error);
        reject(error);
      },
    });
  });
};

/* -------------------------------------------------------------------------- */
/*                                 MEDIA UTILS                                */
/* -------------------------------------------------------------------------- */
export const uploadImageToUserRepository = async (
  userId: FUser["id"],
  file: File,
  metadata: Pick<FImageMetadata, "aspectRatio" | "derivedFromMediaId" | "crop"> & {
    extension: string;
  },
): Promise<FAdvancedImage> => {
  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.MEDIA_METADATA);
  const docRef = doc(collectionRef);

  try {
    const fileName = `img_${docRef.id}`;
    const uploadResult = await uploadFile({
      file,
      firebaseStorageObj: mediaStorage,
      folder: FolderNames.IMAGES,
      extension: null,
      fileName,
      customMetadata: {
        fileName,
      },
      cacheControl: "public,max-age=31536000", // Cache for 1 year
    });

    const res = await httpsCallable<ICreateImagePlaceholderRequest, ICreateImagePlaceholderResponse>(
      functions,
      "IGetImagePlaceholder", // data:image/webp;base64,<base64-data>
    )({
      fileName: `${FolderNames.IMAGES}/${fileName}`,
    });
    const generatedPlaceholder = res.data.data;

    const imageMetadata: FImageMetadata = {
      userId,
      id: docRef.id,
      url: uploadResult.url,
      derivedFromMediaId: metadata.derivedFromMediaId ?? null,
      aspectRatio: metadata.aspectRatio,
      crop: metadata.crop ?? null,
      type: "image",
      placeholder: `data:image/webp;base64,${generatedPlaceholder}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await setDoc(docRef, imageMetadata);

    return {
      url: uploadResult.url,
      aspectRatio: metadata.aspectRatio,
      id: docRef.id,
      placeholder: `data:image/webp;base64,${generatedPlaceholder}`,
    };
  } catch (error) {
    console.error("Error uploading image to user repository:", error);
    await deleteDoc(docRef);
    throw error;
  }
};

export const getCropMetadata = async (id: string) => {
  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.MEDIA_METADATA);
  const docRef = doc(collectionRef, id);

  const docSnapshot = await getDoc(docRef);

  if (!docSnapshot.exists()) {
    throw new Error(`No crop metadata found for ID: ${id}`);
  }

  const data = docSnapshot.data() as FImageMetadata;

  // This has a parent image
  if (data.derivedFromMediaId) {
    const parentDocRef = doc(collectionRef, data.derivedFromMediaId);
    const parentDocSnapshot = await getDoc(parentDocRef);

    if (!parentDocSnapshot.exists()) {
      throw new Error(`No parent image found for ID: ${data.derivedFromMediaId}`);
    }

    const parentData = parentDocSnapshot.data() as FImageMetadata;

    return {
      crop: data.crop,
      parentUrl: parentData.url, // Use the parent's URL
      parentId: parentData.id,
      aspectRatio: parentData.aspectRatio,
      placeholder: parentData.placeholder,
      searchLabel: parentData.searchLabel,
    };
  }

  return {
    parentUrl: data.url, // Use the current image's URL
    parentId: data.id,
    aspectRatio: data.aspectRatio,
    placeholder: data.placeholder,
    searchLabel: data.searchLabel,
  };
};

export const setSearchLabel = async (id: string, searchLabel: string) => {
  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.MEDIA_METADATA);
  const docRef = doc(collectionRef, id);

  const docSnapshot = await getDoc(docRef);

  if (!docSnapshot.exists()) {
    throw new Error(`No image found for ID: ${id}`);
  }

  await setDoc(
    docRef,
    {
      searchLabel,
      updatedAt: new Date().toISOString(),
    },
    { merge: true },
  );
};

export const getAllImagesFromUserRepository = async (
  userId: FUser["id"],
  options?: {
    offset: number;
    limit: number;
  },
) => {
  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.MEDIA_METADATA);

  if (!options) {
    const docsRef = query(collectionRef, where("userId", "==", userId));
    const querySnapshot = await getDocs(docsRef);
    return querySnapshot.docs.map((doc) => doc.data() as FImageMetadata);
  }

  return [];
};
